import { ApolloClient, InMemoryCache, createHttpLink, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';

export const GRAPHQL_ENDPOINT = 'https://chronoschool.theresianum.ac.at/api'; // Update with your actual GraphQL endpoint
//export const GRAPHQL_ENDPOINT = "http://localhost:1337"; // For local development, use the backend running on localhost

const httpLink = createHttpLink({
  uri: GRAPHQL_ENDPOINT + '/graphql',
});

const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

// Global error link: catch 401 / UNAUTHENTICATED and force re-login instead of letting the app crash
const handleUnauthorized = () => {
  try {
    console.warn('Unauthorized response detected — clearing auth and redirecting to /auth');
    // Mark session as expired so the auth page can show a message
    localStorage.setItem('sessionExpired', '1');
    localStorage.removeItem('token');
    localStorage.removeItem('auth_cache');
    localStorage.removeItem('cachedToken'); // Clear cached token
  } catch (e) {
    console.error('Error clearing local storage after 401:', e);
  }
  // Redirect to auth page so user can login again
  window.location.href = '/auth';
};

const errorLink = onError(({ graphQLErrors, networkError }) => {
  // networkError may include status or statusCode depending on environment
  if (networkError) {
    const status = networkError.status || networkError.statusCode || (networkError.response && networkError.response.status);
    if (status === 401) {
      handleUnauthorized();
      return;
    }
  }

  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      // Common pattern for unauthenticated GraphQL errors
      if (err.extensions && (err.extensions.code === 'UNAUTHENTICATED' || err.extensions.code === 'FORBIDDEN')) {
        handleUnauthorized();
        break;
      }
    }
  }
});

const client = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          allUsers: {
            // Cache by variables to prevent unnecessary refetches
            keyArgs: ['userTypes', 'search'],
            // Properly merge arrays by replacing them
            merge(existing, incoming) {
              return incoming;
            },
          },
          allSchoolClasses: {
            // Properly handle array merging
            merge(existing, incoming) {
              return incoming;
            },
          },
          allTeachers: {
            // Properly handle array merging
            merge(existing, incoming) {
              return incoming;
            },
          }
        },
      },
    },
  }),
  // Set default fetch policy to 'no-cache' if not stated otherwise
  defaultOptions: {
    query: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
});

export default client;
